import React, { useState, useEffect, useCallback } from 'react';
import { Terminal, CaretUp, CaretDown, ArrowsClockwise, SpinnerGap, XCircle, Info, Clock } from '@phosphor-icons/react';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';
import { listActivities } from '@/api/projects';

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
};

const TerminalDrawer = ({
  projectId,
  activeProjectId,
  project,
  events = [],
  onRunCommand,
  isOpen: propIsOpen,
  onToggle: propOnToggle
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = propIsOpen !== undefined ? propIsOpen : internalIsOpen;

  const handleToggle = () => {
    if (typeof propOnToggle === 'function') {
      propOnToggle(!isOpen);
    } else {
      setInternalIsOpen(!isOpen);
    }
  };

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedActivityId, setExpandedActivityId] = useState(null);

  const targetProjectId = projectId || activeProjectId || project?.id || project?.project_id;

  const fetchActivities = useCallback(async (pid) => {
    if (!pid) {
      setActivities([]);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await listActivities(pid, 50);
      setActivities(Array.isArray(data) ? data : []);
    } catch (err) {
      setActivities([]);
      const status = err.response?.status;
      const detail =
        status === 401
          ? 'Sesión no válida o no autenticada.'
          : status === 404
          ? 'Proyecto no disponible.'
          : 'Error al consultar actividad del proyecto.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (targetProjectId) {
      fetchActivities(targetProjectId);
    } else {
      setActivities([]);
      setError(null);
    }
  }, [targetProjectId, fetchActivities]);

  return (
    <div
      data-testid="operations-activity-console"
      className={`w-full transition-all duration-300 ${isOpen ? 'h-64 shrink-0' : 'h-9 shrink-0'} bg-[#0B0D13] border-t border-white/[0.08] text-slate-200 flex flex-col min-h-0 overflow-hidden select-none`}
    >
      {/* Header Bar */}
      <div
        onClick={handleToggle}
        className="h-9 px-4 bg-[#0E1117] hover:bg-[#12151E] flex items-center justify-between cursor-pointer shrink-0 transition-colors border-b border-white/[0.04]"
      >
        <div className="flex items-center gap-3">
          <Terminal className="text-cyan-400 w-4 h-4" />
          <span className="font-sans font-medium text-xs text-slate-200 tracking-tight">
            Actividad
          </span>
          {targetProjectId && (
            <span className="text-[11px] text-slate-400 font-sans">
              ({activities.length} {activities.length === 1 ? 'evento' : 'eventos'})
            </span>
          )}
          <OperationalStatusBadge
            status={OPERATIONAL_STATUS.REAL}
            label="REAL / AUDIT LOG"
            size="sm"
          />
        </div>

        <div className="flex items-center gap-3">
          {targetProjectId && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                fetchActivities(targetProjectId);
              }}
              disabled={loading}
              className="p-1 hover:bg-white/[0.06] text-slate-400 hover:text-white rounded transition-colors text-xs flex items-center gap-1"
              title="Recargar eventos reales"
            >
              <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-400 font-sans font-medium transition-colors"
          >
            {isOpen ? (
              <>
                <CaretDown size={14} /> Minimizar
              </>
            ) : (
              <>
                <CaretUp size={14} /> Expandir
              </>
            )}
          </button>
        </div>
      </div>

      {/* Body & Output Area */}
      {isOpen && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[#0A0C10]">
          <div className="flex-1 p-3 overflow-y-auto space-y-2 text-xs font-mono min-h-0">
            {!targetProjectId ? (
              <div className="py-8 text-center text-[#9CA3AF] text-xs font-mono">
                <Info size={20} className="mx-auto mb-2 text-[#6B7280]" />
                <p>Selecciona un proyecto para ver su actividad.</p>
              </div>
            ) : loading ? (
              <div className="py-8 flex flex-col items-center justify-center gap-2 text-[#9CA3AF] text-xs font-mono">
                <SpinnerGap size={20} className="animate-spin text-[#00E5FF]" />
                <span>Cargando actividad del proyecto...</span>
              </div>
            ) : error ? (
              <div className="p-3 bg-[#1C1014] border border-[#F85149]/30 rounded text-xs font-mono text-[#F85149] flex items-start gap-2">
                <XCircle size={16} className="shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold block">Error al consultar actividad</span>
                  <span className="text-[#9CA3AF]">{error}</span>
                </div>
              </div>
            ) : activities.length === 0 ? (
              <div className="py-8 text-center text-[#9CA3AF] text-xs font-mono">
                <Clock size={20} className="mx-auto mb-2 text-[#4B5563]" />
                <p>No hay actividad registrada todavía.</p>
                <p className="text-[11px] text-[#6B7280] mt-1">Las operaciones sobre el proyecto se registrarán aquí.</p>
              </div>
            ) : (
              <div className="space-y-1.5 font-mono text-xs">
                {activities.map((act, idx) => {
                  const hasPayload = act.payload && typeof act.payload === 'object' && Object.keys(act.payload).length > 0;
                  const isExpanded = expandedActivityId === (act.activity_id || idx);
                  return (
                    <div
                      key={act.activity_id || idx}
                      className="p-2 bg-[#13131A] hover:bg-[#1C1C24] border border-[#1F2937]/80 rounded transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[#00E5FF] font-bold tracking-wide">
                            [{act.event_type}]
                          </span>
                          {act.operation_id && (
                            <span className="text-[#6B7280] text-[10px]">
                              op: {act.operation_id.slice(0, 8)}
                            </span>
                          )}
                        </div>
                        <span className="text-[#9CA3AF] text-[11px]">
                          {formatTimestamp(act.occurred_at)}
                        </span>
                      </div>
                      {hasPayload && (
                        <div className="mt-1">
                          <button
                            type="button"
                            onClick={() => setExpandedActivityId(isExpanded ? null : (act.activity_id || idx))}
                            className="text-[10px] text-[#00E5FF] hover:underline flex items-center gap-1 cursor-pointer"
                          >
                            {isExpanded ? '▾ Ocultar detalles' : '▸ Ver detalles'}
                          </button>
                          {isExpanded && (
                            <pre className="mt-1 p-2 bg-[#0A0C10] border border-[#1F2937] rounded text-[10px] text-[#9CA3AF] overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(act.payload, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminalDrawer;
